/**
 * Nostr session management utilities.
 * secretKey is kept in memory only — never written to any persistent store.
 */
import { finalizeEvent, verifyEvent } from 'nostr-tools'
import type { NostrSession } from '@/types/nostr'

const PUBKEY_STORAGE_KEY = 'mackinac:nostrPubkey'

export const NOSTR_SIGNING_ERROR = 'No secret key and no NIP-07 extension available'

export const DEFAULT_RELAYS: string[] = [
    'wss://relay.damus.io',
    'wss://nos.lol',
    'wss://relay.nostr.band',
]

/** Validate a relay URL — must use the wss:// scheme and not target private/loopback hosts */
export function isValidRelayUrl(url: string): boolean {
    try {
        const parsed = new URL(url)
        if (parsed.protocol !== 'wss:') return false
        const hostname = parsed.hostname
        if (!hostname) return false
        // Block private/loopback ranges (SSRF prevention)
        if (hostname === 'localhost') return false
        if (/^127\./.test(hostname)) return false
        if (/^10\./.test(hostname)) return false
        if (/^192\.168\./.test(hostname)) return false
        if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return false
        if (hostname === '169.254.169.254') return false
        if (hostname === '[::1]' || hostname === '::1') return false
        return true
    } catch {
        return false
    }
}

/** Filter and cap relay list to valid wss:// URLs, max 10 */
export function sanitizeRelays(relays: string[]): string[] {
    return relays.filter(isValidRelayUrl).slice(0, 10)
}

/**
 * Restore a NostrSession from localStorage.
 * Only the pubkey is persisted — secretKey is always null on restore.
 */
export function getStoredSession(): NostrSession | null {
    try {
        const stored = localStorage.getItem(PUBKEY_STORAGE_KEY)
        if (!stored) return null
        try {
            const data = JSON.parse(stored)
            return {
                pubkey: data.pubkey,
                secretKey: null,
                displayName: data.displayName,
                avatar: data.avatar,
            }
        } catch {
            // Legacy format: plain pubkey string
            return { pubkey: stored, secretKey: null }
        }
    } catch {
        return null
    }
}

/** Persist session pubkey to localStorage. secretKey is NOT persisted. */
export function saveSession(session: NostrSession): void {
    try {
        localStorage.setItem(
            PUBKEY_STORAGE_KEY,
            JSON.stringify({
                pubkey: session.pubkey,
                displayName: session.displayName,
                avatar: session.avatar,
            })
        )
    } catch {
        // localStorage unavailable
    }
}

/** Clear Nostr session from localStorage and zero-fill secretKey. */
export function clearSession(session?: NostrSession | null): void {
    try {
        localStorage.removeItem(PUBKEY_STORAGE_KEY)
    } catch {
        // localStorage unavailable
    }
    if (session?.secretKey) {
        session.secretKey.fill(0)
    }
}

export interface NostrEventTemplate {
    kind: number
    content: string
    tags: string[][]
    created_at?: number
}

/**
 * Create and sign a Nostr event.
 * Uses secretKey if present, otherwise attempts NIP-07 extension signing.
 */
export async function createSignedEvent(
    session: NostrSession,
    kind: number,
    content: string,
    tags: string[][]
): Promise<ReturnType<typeof finalizeEvent>> {
    const template = {
        kind,
        content,
        tags,
        created_at: Math.floor(Date.now() / 1000),
    }

    if (session.secretKey && session.secretKey.length > 0) {
        return finalizeEvent(template, session.secretKey)
    }

    // NIP-07 path: use browser extension
    const nostrExt = (window as { nostr?: { signEvent: (e: unknown) => Promise<unknown> } }).nostr
    if (!nostrExt) {
        throw new Error(NOSTR_SIGNING_ERROR)
    }
    const signed = await nostrExt.signEvent({ ...template, pubkey: session.pubkey })
    const typedSigned = signed as ReturnType<typeof finalizeEvent>
    if (!verifyEvent(typedSigned as Parameters<typeof verifyEvent>[0])) {
        throw new Error('NIP-07 extension returned an invalid event signature')
    }
    return typedSigned
}
